import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Grid,
  Stack,
} from "@mui/material";
import {
  CasinoOutlined as DiceIcon,
  EmojiEventsOutlined as PrizeIcon,
  SecurityOutlined as ShieldIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";

const AllLotteriesHero = ({
  totalActiveCount,
  totalPrizePool,
  currency,
}) => {
  return (
    <Card
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        mb: 4,
      }}
    >
      {/* Top Gradient Stripe */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: colors.gradients.primary,
        }}
      />

      <Grid container spacing={3} alignItems="center">
        {/* Left Side: Title & Description */}
        <Grid item xs={12} lg={7}>
          <Stack spacing={1.5}>
            {/* Live Indicator Badge */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.35,
                  borderRadius: borderRadiusPx.full,
                  bgcolor: colors.emerald[50],
                  border: `1px solid ${colors.emerald[200]}`,
                }}
              >
                <Box className="live-indicator-dot" />
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.emerald[700],
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: "0.72rem",
                    letterSpacing: "0.5px",
                  }}
                >
                  On-Chain Live Draws
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.25,
                  py: 0.35,
                  borderRadius: borderRadiusPx.full,
                  bgcolor: colors.blue[50],
                  border: `1px solid ${colors.blue[200]}`,
                }}
              >
                <ShieldIcon sx={{ fontSize: 13, color: colors.primary.main }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.primary.dark,
                    fontWeight: 700,
                    fontSize: "0.72rem",
                  }}
                >
                  Provably Fair VRF
                </Typography>
              </Box>
            </Stack>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                color: colors.slate[900],
                letterSpacing: "-0.5px",
                fontSize: { xs: "1.85rem", sm: "2.35rem", md: "2.6rem" },
              }}
            >
              All Lotteries & Live Pools
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: colors.slate[600],
                fontSize: { xs: "0.925rem", sm: "1rem" },
                maxWidth: 620,
                lineHeight: 1.6,
              }}
            >
              Participate in transparent decentralized lotteries across MegaBET, SpinBET, MineBET, and RocketBET. Guaranteed on-chain execution with instant automated prize payouts.
            </Typography>
          </Stack>
        </Grid>

        {/* Right Side: Live Metrics Summary Box */}
        <Grid item xs={12} lg={5}>
          <Grid container spacing={2}>
            {/* Stat 1: Active Draws */}
            <Grid item xs={6}>
              <Box
                className="hero-metric-card"
                sx={{
                  p: 2.5,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.slate[50],
                  border: `1px solid ${colors.slate[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.blue[50],
                      color: colors.primary.main,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <DiceIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.slate[600],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.72rem",
                    }}
                  >
                    Active Lotteries
                  </Typography>
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 900, color: colors.slate[900], fontSize: "1.65rem" }}>
                  {totalActiveCount}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.emerald[600], fontWeight: 600 }}>
                  Ready to play
                </Typography>
              </Box>
            </Grid>

            {/* Stat 2: Total Prize Pool */}
            <Grid item xs={6}>
              <Box
                className="hero-metric-card"
                sx={{
                  p: 2.5,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.blue[50],
                  border: `1px solid ${colors.blue[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.primary.main,
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PrizeIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.slate[600],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.72rem",
                    }}
                  >
                    Total Prize Pool
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    color: colors.primary.dark,
                    fontSize: "1.65rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {totalPrizePool} {currency}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.primary.main, fontWeight: 600 }}>
                  Across active pools
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

AllLotteriesHero.propTypes = {
  totalActiveCount: PropTypes.number.isRequired,
  totalPrizePool: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
};

export default AllLotteriesHero;
